import React, { useEffect, useRef, useState } from 'react'
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'

import cogoToast from 'cogo-toast'
import DOMPurify from 'dompurify'

import CasesService from '../../../services/CasesService'
import format from '../../../services/format'
import { getToken } from '../../../utils/getToken'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'
import ConfirmationModal from '../../controls/ConfirmationModal'
import DNPTable from '../../DNPTable'

const normalizeStatusKey = status => {
  if (!status) return ''
  const sLower = String(status).trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')
  switch (sLower) {
    case 'in-progress':
    case 'inprogress':
    case 'draft':
      return 'in-progress'
    case 'submitted':
    case 'under-review':
    case 'in-review':
    case 'sent-to-administrator':
    case 'sent-to-reviewer':
      return 'submitted'
    case 'in-smart-review':
    case 'insmartreview':
      return 'in_smart_review'
    case 'approved':
    case 'saved-and-completed':
      return 'approved'
    case 'rejected':
    case 'denied':
    case 'returned':
      return 'rejected'
    default:
      return String(status).trim().toLowerCase()
  }
}

/**
 * Maps a canonical status key (e.g. 'submitted') to the tenant's display label.
 * Falls back to a human-readable version of the key if settings are unavailable.
 */
const getStatusDisplay = (status, settings) => {
  const canonical = normalizeStatusKey(status)
  const cs = settings?.caseStatuses
  if (cs) {
    const map = {
      'in-progress': cs.inProgress?.statusText,
      submitted: cs.submitted?.statusText,
      approved: cs.approved?.statusText,
      rejected: cs.rejected?.statusText,
      closed: cs.closed?.statusText,
      canceled: cs.canceled?.statusText,
      filed: cs.filed?.statusText,
      final: cs.final?.statusText,
      invoiced: cs.invoiced?.statusText,
      in_smart_review: 'In Smart Review',
    }
    if (map[canonical]) return map[canonical]
    if (map[status]) return map[status]
    if (map[status?.toLowerCase()]) return map[status.toLowerCase()]
  }
  if (canonical === 'in_smart_review') return 'In Smart Review'
  // Fallback: convert 'in-progress' → 'In Progress'
  return status ? status.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : ''
}

const Component = ({
  caseNumber,
  showError,
  isCaseClosed,
  isDraft,
  settings,
  allowedReportTypes,
  caseTypeKey,
  disableFileReport,
  user,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [reports, setReports] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [showAdminTools, setShowAdminTools] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  const history = useHistory()

  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [submissionNote, setSubmissionNote] = useState('')

  const [smartReviewPrompt, setSmartReviewPrompt] = useState('')
  const [isSavingPrompt, setIsSavingPrompt] = useState(false)
  const [testReportNumber, setTestReportNumber] = useState('')
  const [isTestingPrompt, setIsTestingPrompt] = useState(false)
  const [testResults, setTestResults] = useState(null)

  const [appointmentsNarrativePrompt, setAppointmentsNarrativePrompt] = useState('')
  const [isSavingNarrativePrompt, setIsSavingNarrativePrompt] = useState(false)
  const [testNarrativeReportNumber, setTestNarrativeReportNumber] = useState('')
  const [isTestingNarrativePrompt, setIsTestingNarrativePrompt] = useState(false)
  const [testNarrativeResults, setTestNarrativeResults] = useState(null)

  const [richTextFormattingPrompt, setRichTextFormattingPrompt] = useState('')
  const [isSavingFormattingPrompt, setIsSavingFormattingPrompt] = useState(false)
  const [testFormattingSampleText, setTestFormattingSampleText] = useState(
    'Claimant is a 44-year-old warehouse associate who sustained an acute lower back and right shoulder injury on 03/14/2026 while lifting a 65 lb pallet. Presented to St. Mary Urgent Care complaining of 7/10 radiating right lumbar pain and limited right shoulder abduction to 85 degrees. Lumbar spine MRI on 03/22/2026 demonstrated an L4-L5 right paracentral disc protrusion with mild right L5 nerve root impingement and no high-grade central canal stenosis. Placed on modified duty with a 15 lb lifting restriction, no overhead reaching with the right upper extremity, and referred for 6 weeks of physical therapy.',
  )
  const [isTestingFormattingPrompt, setIsTestingFormattingPrompt] = useState(false)
  const [testFormattingResults, setTestFormattingResults] = useState(null)

  const [editingReport, setEditingReport] = useState(null)
  const [editFormData, setEditFormData] = useState({
    reportType: 'Progress',
    sequenceNumber: 1,
    startUtc: '',
    endUtc: '',
    status: 'filed',
  })
  const [isSavingReport, setIsSavingReport] = useState(false)
  const editTypeRef = useRef(null)

  const canUpdateCase = Permissions.can(user, CAPABILITIES.UPDATE_CASE)
  const canEngineerPrompt = Permissions.can(user, CAPABILITIES.PROMPT_ENGINEER)
  const canResetReportingCycle = canUpdateCase || Permissions.can(user, CAPABILITIES.DELETE_CASE)
  const canManageReports = canResetReportingCycle || canUpdateCase
  const showDiagnosticsButton = canEngineerPrompt || canManageReports

  const onFileInitialReportClick = () => {
    setSubmissionNote('')
    setShowSubmitModal(true)
  }

  const onConfirmSubmit = async () => {
    try {
      setShowSubmitModal(false)
      setSubmitting(true)
      const result = await CasesService.submitReport(caseNumber, submissionNote)
      if (result && result.reportNumber) {
        history.push(`/cases/${caseNumber}/reports/${result.reportNumber}`)
      }
    } catch (e) {
      alert('Failed to create Initial Report')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResetCycle = () => {
    setConfirmAction({
      title: 'Reset Progress Reporting Cycle',
      body: 'This will delete all progress and final reports for this case, and release all of their logged times/expenses back to the unbilled pool so that new reports will include them. This action preserves an Initial Report if there is one, and allows you to rebuild the timeline correctly from the start.',
      onConfirm: async () => {
        setConfirmAction(null)
        try {
          setIsLoading(true)
          await CasesService.resetReportingCycle(caseNumber)
          cogoToast.success('Progress reporting cycle reset successfully.')
          loadReports()
        } catch (e) {
          showError(e.message || 'Failed to reset reporting cycle')
          setIsLoading(false)
        }
      },
    })
  }

  const handleUnlock = reportNumber => {
    setConfirmAction({
      title: 'Unlock Report',
      body: `Are you sure you want to unlock Report #${reportNumber}?`,
      onConfirm: async () => {
        setConfirmAction(null)
        try {
          setIsLoading(true)
          await CasesService.unlockReport(caseNumber, reportNumber)
          cogoToast.success(`Report #${reportNumber} unlocked successfully.`)
          loadReports()
        } catch (e) {
          showError(e.message || 'Failed to unlock report')
          setIsLoading(false)
        }
      },
    })
  }

  const handleSyncSnapshot = reportNumber => {
    setConfirmAction({
      title: 'Sync Snapshot',
      body: `Are you sure you want to force-regenerate the snapshot for Report #${reportNumber} to sync it with latest case details?`,
      onConfirm: async () => {
        setConfirmAction(null)
        try {
          setIsLoading(true)
          await CasesService.regenerateSnapshot(caseNumber, reportNumber)
          cogoToast.success(`Report #${reportNumber} snapshot regenerated successfully.`)
          loadReports()
        } catch (e) {
          showError(e.message || 'Failed to sync snapshot')
          setIsLoading(false)
        }
      },
    })
  }

  const handleDeleteReport = reportNumber => {
    setConfirmAction({
      title: 'Delete Report',
      body: `Are you sure you want to delete Report #${reportNumber}? This will release its logged times back to the unbilled pool.`,
      onConfirm: async () => {
        setConfirmAction(null)
        try {
          setIsLoading(true)
          await CasesService.deleteReport(caseNumber, reportNumber)
          cogoToast.success(`Report #${reportNumber} deleted successfully.`)
          loadReports()
        } catch (e) {
          showError(e.message || 'Failed to delete report')
          setIsLoading(false)
        }
      },
    })
  }

  const handleOpenEditModal = report => {
    setEditingReport(report)
    setEditFormData({
      reportType: report.reportType || (report.reportNumber <= 1 ? 'Initial' : 'Progress'),
      sequenceNumber: report.sequenceNumber || (report.reportNumber > 1 ? report.reportNumber - 1 : 1),
      startUtc: report.fromDateTimeUtc ? String(report.fromDateTimeUtc).substring(0, 10) : '',
      endUtc: report.toDateTimeUtc ? String(report.toDateTimeUtc).substring(0, 10) : '',
      status: normalizeStatusKey(report.status) || 'filed',
    })
  }

  const handleSaveReportDetails = async () => {
    if (!editingReport) return
    setIsSavingReport(true)
    try {
      const payload = {
        sequenceNumber: parseInt(editFormData.sequenceNumber, 10) || 1,
        reportType: editFormData.reportType,
        startUtc: editFormData.startUtc ? new Date(`${editFormData.startUtc}T00:00:00Z`).toISOString() : null,
        endUtc: editFormData.endUtc ? new Date(`${editFormData.endUtc}T23:59:59Z`).toISOString() : null,
        status: editFormData.status,
      }
      await CasesService.updateReport(caseNumber, editingReport.reportNumber, payload)
      cogoToast.success('Report details updated successfully!')
      setEditingReport(null)
      loadReports()
    } catch (err) {
      showError(err.message || 'Failed to update report details.')
    } finally {
      setIsSavingReport(false)
    }
  }

  const loadReports = () => {
    setIsLoading(true) // always reset — prevents stale 'No Reports' on dep changes

    if (isDraft) {
      setIsLoading(false)
      return
    }

    const inProgressReportPromise = !isCaseClosed ? CasesService.getInProgressReport(caseNumber) : Promise.resolve(null)

    const reportsPromise = CasesService.getReportsForCase(caseNumber, [
      'reportNumber',
      'submittedOnUtc',
      'fromDateTimeUtc',
      'toDateTimeUtc',
      'totalBillableTime',
      'status',
      'reportType',
      'sequenceNumber',
      'isExternal',
      'documentUrl',
      'pdfUrl',
      'fileMetadataId',
    ])

    Promise.all([inProgressReportPromise, reportsPromise])
      .then(results => {
        const inProgressReport = results[0]
        const dbReports = results[1] || []

        // If inProgressReport collides with a DB report that is already submitted/approved/filed, prefer the DB report's status
        const collidingNonDraftDbReport = inProgressReport
          ? dbReports.find(
              r =>
                r.reportNumber === inProgressReport.reportNumber &&
                r.status !== 'in-progress' &&
                r.status !== 'InProgress',
            )
          : null

        const effectiveInProgress = collidingNonDraftDbReport ? null : inProgressReport

        // If effectiveInProgress exists, deduplicate any pending reports from dbReports
        const dbWithoutDuplicateInProgress = effectiveInProgress
          ? dbReports.filter(
              r =>
                r.reportNumber !== effectiveInProgress.reportNumber &&
                r.status !== 'in-progress' &&
                r.status !== 'InProgress',
            )
          : dbReports

        const fetchedReports = [effectiveInProgress, ...dbWithoutDuplicateInProgress].filter(Boolean)

        // Deduplicate reports by reportNumber
        const uniqueReports = Array.from(new Map(fetchedReports.map(item => [item.reportNumber, item])).values())

        uniqueReports.sort((a, b) => {
          const aIsPending = a.status === 'in-progress' || a.status === 'InProgress'
          const bIsPending = b.status === 'in-progress' || b.status === 'InProgress'
          if (aIsPending && !bIsPending) return -1
          if (!aIsPending && bIsPending) return 1

          const aIsInitial = a.reportType === 'Initial' || (!a.reportType && a.reportNumber <= 1)
          const bIsInitial = b.reportType === 'Initial' || (!b.reportType && b.reportNumber <= 1)
          if (aIsInitial && !bIsInitial) return 1
          if (!aIsInitial && bIsInitial) return -1

          const aIsFinal = a.reportType === 'Final' || a.status === 'final'
          const bIsFinal = b.reportType === 'Final' || b.status === 'final'
          if (aIsFinal && !bIsFinal) return -1
          if (!aIsFinal && bIsFinal) return 1

          // Compare by toDateTimeUtc desc
          const aTo = a.toDateTimeUtc ? new Date(a.toDateTimeUtc).getTime() : 0
          const bTo = b.toDateTimeUtc ? new Date(b.toDateTimeUtc).getTime() : 0
          if (aTo !== bTo) return bTo - aTo

          // Compare by fromDateTimeUtc desc
          const aFrom = a.fromDateTimeUtc ? new Date(a.fromDateTimeUtc).getTime() : 0
          const bFrom = b.fromDateTimeUtc ? new Date(b.fromDateTimeUtc).getTime() : 0
          if (aFrom !== bFrom) return bFrom - aFrom

          // Compare by sequenceNumber desc
          const aSeq = a.sequenceNumber ?? 0
          const bSeq = b.sequenceNumber ?? 0
          if (aSeq !== bSeq) return bSeq - aSeq

          return (b.reportNumber ?? 0) - (a.reportNumber ?? 0)
        })

        setReports(uniqueReports)
      })
      .catch(() => {
        showError('An unexpected error occurred while attempting to get the list of reports')
      })
      .then(() => setIsLoading(false))
  }

  useEffect(() => {
    loadReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber, showError, isCaseClosed, isDraft])

  useEffect(() => {
    if (canEngineerPrompt && showAdminTools) {
      CasesService.getSmartReviewPrompt()
        .then(res => setSmartReviewPrompt(res.prompt || ''))
        .catch(err => {
          const msg = err.message || 'Failed to load AI prompt template.'
          cogoToast.error(msg)
          showError(msg)
        })

      CasesService.getAppointmentsNarrativePrompt()
        .then(res => setAppointmentsNarrativePrompt(res.prompt || ''))
        .catch(err => {
          const msg = err.message || 'Failed to load AI appointments narrative prompt template.'
          cogoToast.error(msg)
          showError(msg)
        })

      CasesService.getRichTextFormattingPrompt()
        .then(res => setRichTextFormattingPrompt(res.prompt || ''))
        .catch(err => {
          const msg = err.message || 'Failed to load AI rich text formatting prompt template.'
          cogoToast.error(msg)
          showError(msg)
        })
    }
  }, [canEngineerPrompt, showAdminTools, showError])

  const handleSavePrompt = async () => {
    setIsSavingPrompt(true)
    try {
      await CasesService.saveSmartReviewPrompt(smartReviewPrompt)
      cogoToast.success('AI Prompt Template saved successfully!')
    } catch (err) {
      const msg = err.message || 'Failed to save prompt template.'
      cogoToast.error(msg)
      showError(msg)
    } finally {
      setIsSavingPrompt(false)
    }
  }

  const handleTestPrompt = async () => {
    if (!testReportNumber) {
      cogoToast.warn('Please enter a Report Number to test.')
      return
    }
    setIsTestingPrompt(true)
    setTestResults(null)
    try {
      const res = await CasesService.testSmartReviewPrompt(
        caseNumber,
        parseInt(testReportNumber, 10),
        smartReviewPrompt,
      )
      setTestResults(res.suggestions || [])
      cogoToast.success('AI prompt tested successfully!')
    } catch (err) {
      const msg = err.message || 'Prompt test failed.'
      cogoToast.error(msg)
      showError(msg)
    } finally {
      setIsTestingPrompt(false)
    }
  }

  const handleSaveAppointmentsNarrativePrompt = async () => {
    setIsSavingNarrativePrompt(true)
    try {
      await CasesService.saveAppointmentsNarrativePrompt(appointmentsNarrativePrompt)
      cogoToast.success('AI Appointments Narrative Prompt Template saved successfully!')
    } catch (err) {
      const msg = err.message || 'Failed to save narrative prompt template.'
      cogoToast.error(msg)
      showError(msg)
    } finally {
      setIsSavingNarrativePrompt(false)
    }
  }

  const handleTestAppointmentsNarrativePrompt = async () => {
    if (!testNarrativeReportNumber) {
      cogoToast.warn('Please enter a Report Number to test.')
      return
    }
    setIsTestingNarrativePrompt(true)
    setTestNarrativeResults(null)
    try {
      const res = await CasesService.testAppointmentsNarrativePrompt(
        caseNumber,
        parseInt(testNarrativeReportNumber, 10),
        appointmentsNarrativePrompt,
      )
      setTestNarrativeResults(res || null)
      cogoToast.success('AI appointments narrative prompt tested successfully!')
    } catch (err) {
      const msg = err.message || 'Narrative prompt test failed.'
      cogoToast.error(msg)
      showError(msg)
    } finally {
      setIsTestingNarrativePrompt(false)
    }
  }

  const handleSaveRichTextFormattingPrompt = async () => {
    setIsSavingFormattingPrompt(true)
    try {
      await CasesService.saveRichTextFormattingPrompt(richTextFormattingPrompt)
      cogoToast.success('AI Rich Text Formatting Prompt Template saved successfully!')
    } catch (err) {
      const msg = err.message || 'Failed to save rich text formatting prompt template.'
      cogoToast.error(msg)
      showError(msg)
    } finally {
      setIsSavingFormattingPrompt(false)
    }
  }

  const handleTestRichTextFormattingPrompt = async () => {
    if (!testFormattingSampleText || !testFormattingSampleText.trim()) {
      cogoToast.warn('Please enter sample plain text to test.')
      return
    }
    setIsTestingFormattingPrompt(true)
    setTestFormattingResults(null)
    try {
      const res = await CasesService.testRichTextFormattingPrompt(testFormattingSampleText, richTextFormattingPrompt)
      setTestFormattingResults(res || null)
      cogoToast.success('AI rich text formatting prompt tested successfully!')
    } catch (err) {
      const msg = err.message || 'Rich text formatting prompt test failed.'
      cogoToast.error(msg)
      showError(msg)
    } finally {
      setIsTestingFormattingPrompt(false)
    }
  }

  if (isDraft) {
    return (
      <div className="container mt-4 text-center text-muted">
        <p className="mb-1">
          <strong>No reports yet.</strong>
        </p>
        {allowedReportTypes?.includes('Initial') && (
          <p>Reports will be generated automatically when the case is activated.</p>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center mt-2">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  const columns = [
    {
      title: 'Report Name',
      getText: report => {
        let reportTitle
        if (report.reportType === 'Final') {
          reportTitle = 'Final Report'
        } else if (report.reportType === 'Initial') {
          reportTitle = 'Initial Case Report'
        } else if (report.reportType === 'CaseManagement') {
          reportTitle = 'Case Management Report'
        } else {
          reportTitle = `Progress Report ${report.sequenceNumber}`
        }

        // Fallback logic for old reports that might not have type populated yet (though migration should have fixed it)
        if (!report.reportType) {
          if (report.status === 'final') {
            reportTitle = 'Final Report'
          } else {
            reportTitle =
              report.reportNumber <= 1 ? 'Initial Case Report' : `Progress Report ${report.reportNumber - 1}`
          }
        }

        if (report.isExternal) {
          const fileName = report.pdfUrl || report.documentUrl
          const token = getToken()
          const fileDownloadUrl = fileName
            ? `/api/cases/${caseNumber}/sharefile-files/content/${encodeURIComponent(fileName)}?access_token=${token}`
            : null

          return (
            <span>
              {fileDownloadUrl ? (
                <a href={fileDownloadUrl} target="_blank" rel="noopener noreferrer">
                  {reportTitle}
                </a>
              ) : (
                <span>{reportTitle}</span>
              )}
              <span className="badge badge-secondary ml-2 font-weight-normal">ext</span>
            </span>
          )
        }

        if ((report.status === 'in-progress' || report.status === 'rejected') && report.status !== 'final') {
          return <Link to={`/cases/${caseNumber}/reports/in-progress`}>{reportTitle}</Link>
        } else {
          return <Link to={`/cases/${caseNumber}/reports/${report.reportNumber}`}>{reportTitle}</Link>
        }
      },
    },
    {
      title: 'Activity Dates',
      getText: report => {
        const from = format.date(report.fromDateTimeUtc)
        const to = format.date(report.toDateTimeUtc)
        if (from && to) return `${from} - ${to}`
        if (from) return from
        if (to) return to
        return <>&mdash;</>
      },
    },
    {
      title: 'Billable hours',
      getText: report =>
        report.isExternal ? (
          <>&mdash;</>
        ) : report.totalBillableTime != null ? (
          Math.round(((report.totalBillableTime || 0) + Number.EPSILON) * 100) / 100
        ) : (
          ''
        ),
      className: 'text-center',
    },
    {
      title: 'Status',
      getText: report => {
        if (report.status === 'final') return getStatusDisplay('final', settings)
        if (report.reportNumber <= 1 && report.status === 'submitted') return getStatusDisplay('submitted', settings)
        const display = getStatusDisplay(report.status, settings)
        if (report.status === 'approved') {
          return (
            <span>
              {display}
              <span
                className="badge ml-2 font-weight-normal text-dark"
                style={{ backgroundColor: '#fff3cd', borderColor: '#ffeeba', border: '1px solid #ffeeba' }}
              >
                Awaiting Invoicing
              </span>
            </span>
          )
        }
        return display
      },
    },
    {
      title: 'Submitted On',
      getText: report => (report.submittedOnUtc ? format.date(report.submittedOnUtc) : <>&mdash;</>),
    },
  ]

  if (canManageReports && showAdminTools) {
    columns.push({
      title: 'Admin Actions',
      className: 'text-center',
      getText: report => (
        <div className="d-flex justify-content-center align-items-center" style={{ gap: '0.5rem' }}>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-edit-${report.reportNumber}`}>
                Edit report sequence number, report type, activity date range, and status directly.
              </Tooltip>
            }
          >
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleOpenEditModal(report)}
              title="Edit report details"
            >
              <i className="fa fa-edit" style={{ pointerEvents: 'none' }} />
            </button>
          </OverlayTrigger>
          {report.status !== 'in-progress' && report.status !== 'rejected' && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-unlock-${report.reportNumber}`}>
                  Reverts a submitted or locked report back to &quot;In Progress&quot; status, allowing case managers to
                  edit its fields and dates in the interactive viewer. This is constrained by the dates of preceding and
                  subsequent locked reports.
                </Tooltip>
              }
            >
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => handleUnlock(report.reportNumber)}
                title="Unlock report"
              >
                <i className="fa fa-lock" style={{ pointerEvents: 'none' }} />
              </button>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-sync-${report.reportNumber}`}>
                Synchronizes the report&apos;s static case data snapshot (e.g., claimant/adjuster addresses, employer
                details) to match the current values in Case Details. Use this if details were edited after the report
                was created.
              </Tooltip>
            }
          >
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleSyncSnapshot(report.reportNumber)}
              title="Sync snapshot with latest case details"
            >
              <i className="fa fa-sync" style={{ pointerEvents: 'none' }} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-delete-${report.reportNumber}`}>
                Permanently deletes this specific report from the database and releases all of its linked activities and
                expenses back into the unbilled pool. This is useful for removing duplicate or incorrect progress/final
                reports.
              </Tooltip>
            }
          >
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteReport(report.reportNumber)}
              title="Delete report and release items"
            >
              <i className="fa fa-trash" style={{ pointerEvents: 'none' }} />
            </button>
          </OverlayTrigger>
        </div>
      ),
    })
  }

  return (
    <div className="container mt-2">
      <div className="mt-4 mb-4 text-right">
        {!isCaseClosed &&
          (() => {
            if (disableFileReport) {
              return (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="file-report-disabled-tooltip">
                      To file a final report, Close this case from the Overview tab
                    </Tooltip>
                  }
                >
                  <span className="d-inline-block">
                    <button className="btn btn-primary" disabled style={{ pointerEvents: 'none' }}>
                      File Report
                    </button>
                  </span>
                </OverlayTrigger>
              )
            }
            // Show "File Initial Report" when:
            // (a) the normal case: exactly 1 unsubmitted in-progress report, OR
            // (b) the draft-reopen edge case: every existing report is a Final type
            //     (no Initial was ever filed — the Final came from an erroneous draft close)
            const singleUnsubmitted =
              reports && reports.length === 1 && !reports[0].submittedOnUtc && reports[0].reportType === 'Initial'
            const allFinalNoInitial =
              reports &&
              reports.length > 0 &&
              reports.every(r => r.reportType === 'Final') &&
              allowedReportTypes?.includes('Initial')
            return singleUnsubmitted || allFinalNoInitial ? (
              <button className="btn btn-primary" onClick={onFileInitialReportClick} disabled={submitting}>
                {submitting ? 'Creating...' : 'File Initial Report'}
              </button>
            ) : (
              <Link to={`/cases/${caseNumber}/reports/in-progress`} className="btn btn-primary">
                File Report
              </Link>
            )
          })()}

        {showDiagnosticsButton && (
          <button
            className={`btn ml-2 ${showAdminTools ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setShowAdminTools(!showAdminTools)}
          >
            <i className="fa fa-wrench mr-1" />
            {showAdminTools ? 'Hide Admin & AI Tools' : 'Show Admin & AI Tools'}
          </button>
        )}
      </div>

      <DNPTable items={reports} columns={columns} noItemsText="No Reports" />

      {showDiagnosticsButton && showAdminTools && (
        <div className="card border-danger mt-4">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0 card-title h6">Admin Diagnostics & AI Prompt Tools</h5>
          </div>
          <div className="card-body">
            {canResetReportingCycle && (
              <>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="card-text text-muted small mr-3">
                    Deletes all Progress and Final reports on this case, releasing all associated billable hours and
                    expenses back into the unbilled pool so that new reports will include them. This action preserves an
                    Initial Report if there is one, and allows you to rebuild the timeline correctly from the start.
                  </span>
                  <button className="btn btn-danger btn-sm text-nowrap" onClick={handleResetCycle}>
                    <i className="fa fa-redo mr-1" /> Reset Progress Reporting Cycle
                  </button>
                </div>
                {canEngineerPrompt && <hr className="my-4" />}
              </>
            )}

            {canEngineerPrompt && (
              <>
                <h6 className="font-weight-bold text-dark mb-3">
                  <i className="fa fa-magic mr-1" /> AI Smart Review Prompt Refinement
                </h6>

                <div className="form-group mb-3">
                  <label htmlFor="smartReviewPrompt" className="form-label text-muted small font-weight-bold">
                    PROMPT TEMPLATE
                  </label>
                  <textarea
                    id="smartReviewPrompt"
                    className="form-control font-mono"
                    rows={12}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    value={smartReviewPrompt}
                    onChange={e => setSmartReviewPrompt(e.target.value)}
                  />
                </div>

                <div className="text-right mb-4">
                  <button className="btn btn-primary btn-sm" onClick={handleSavePrompt} disabled={isSavingPrompt}>
                    {isSavingPrompt ? 'Saving...' : 'Save Prompt Template'}
                  </button>
                </div>

                <div className="card bg-light border-secondary mb-3">
                  <div className="card-body">
                    <h6 className="card-title font-weight-bold h6 text-secondary mb-3">Test Prompt Template</h6>
                    <div className="form-row align-items-end">
                      <div className="col-md-4">
                        <label htmlFor="testReportNumber" className="form-label text-muted small font-weight-bold">
                          REPORT NUMBER TO TEST AGAINST
                        </label>
                        <input
                          type="number"
                          id="testReportNumber"
                          className="form-control form-control-sm"
                          placeholder="e.g. 1"
                          value={testReportNumber}
                          onChange={e => setTestReportNumber(e.target.value)}
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleTestPrompt}
                          disabled={isTestingPrompt}
                        >
                          {isTestingPrompt ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm mr-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Testing...
                            </>
                          ) : (
                            'Run Test'
                          )}
                        </button>
                      </div>
                    </div>

                    {testResults && (
                      <div className="mt-4">
                        <h6 className="font-weight-bold text-dark mb-2 small">Test Result Suggestions JSON:</h6>
                        <pre
                          className="bg-dark text-white p-3 rounded small"
                          style={{ maxHeight: '300px', overflow: 'auto' }}
                        >
                          {JSON.stringify(testResults, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>

                <hr className="my-4" />

                <h6 className="font-weight-bold text-dark mb-3">
                  <i className="fa fa-magic mr-1" /> AI Appointments Narrative Prompt Refinement
                </h6>

                <div className="form-group mb-3">
                  <label htmlFor="appointmentsNarrativePrompt" className="form-label text-muted small font-weight-bold">
                    PROMPT TEMPLATE
                  </label>
                  <textarea
                    id="appointmentsNarrativePrompt"
                    className="form-control font-mono"
                    rows={10}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    value={appointmentsNarrativePrompt}
                    onChange={e => setAppointmentsNarrativePrompt(e.target.value)}
                  />
                </div>

                <div className="text-right mb-4">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveAppointmentsNarrativePrompt}
                    disabled={isSavingNarrativePrompt}
                  >
                    {isSavingNarrativePrompt ? 'Saving...' : 'Save Narrative Prompt Template'}
                  </button>
                </div>

                <div className="card bg-light border-secondary mb-3">
                  <div className="card-body">
                    <h6 className="card-title font-weight-bold h6 text-secondary mb-3">
                      Test Appointments Narrative Prompt
                    </h6>
                    <div className="form-row align-items-end">
                      <div className="col-md-4">
                        <label
                          htmlFor="testNarrativeReportNumber"
                          className="form-label text-muted small font-weight-bold"
                        >
                          REPORT NUMBER TO TEST AGAINST
                        </label>
                        <input
                          type="number"
                          id="testNarrativeReportNumber"
                          className="form-control form-control-sm"
                          placeholder="e.g. 1"
                          value={testNarrativeReportNumber}
                          onChange={e => setTestNarrativeReportNumber(e.target.value)}
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleTestAppointmentsNarrativePrompt}
                          disabled={isTestingNarrativePrompt}
                        >
                          {isTestingNarrativePrompt ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm mr-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Generating...
                            </>
                          ) : (
                            'Run Test'
                          )}
                        </button>
                      </div>
                    </div>

                    {testNarrativeResults && (
                      <div className="mt-4">
                        <h6 className="font-weight-bold text-dark mb-2 small">
                          Test Result Output ({testNarrativeResults.completedCount || 0} completed appointment
                          {testNarrativeResults.completedCount === 1 ? '' : 's'}):
                        </h6>
                        <div
                          className="bg-white p-3 border rounded small"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(testNarrativeResults.narrative) }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <hr className="my-4" />

                <h6 className="font-weight-bold text-dark mb-2">AI Rich Text Formatting Prompt Refinement</h6>
                <p className="text-muted small mb-3">
                  Customize the AI system prompt used to automatically format extracted plain-text clinical narratives
                  (e.g. Description of Injury, Diagnostic Test Results, Surgery Results, MMI Narrative) into scannable
                  rich text.
                </p>

                <div className="form-group mb-3">
                  <label htmlFor="richTextFormattingPrompt" className="form-label font-weight-bold small text-muted">
                    RICH TEXT FORMATTING SYSTEM PROMPT TEMPLATE
                  </label>
                  <textarea
                    id="richTextFormattingPrompt"
                    className="form-control font-monospace small"
                    rows="10"
                    value={richTextFormattingPrompt}
                    onChange={e => setRichTextFormattingPrompt(e.target.value)}
                  />
                </div>

                <div className="text-right mb-4">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveRichTextFormattingPrompt}
                    disabled={isSavingFormattingPrompt}
                  >
                    {isSavingFormattingPrompt ? 'Saving...' : 'Save Formatting Prompt Template'}
                  </button>
                </div>

                <div className="card bg-light border-secondary mb-3">
                  <div className="card-body">
                    <h6 className="card-title font-weight-bold h6 text-secondary mb-3">
                      Test Rich Text Formatting Prompt
                    </h6>
                    <div className="form-group mb-3">
                      <label
                        htmlFor="testFormattingSampleText"
                        className="form-label text-muted small font-weight-bold"
                      >
                        SAMPLE PLAIN TEXT TO FORMAT
                      </label>
                      <textarea
                        id="testFormattingSampleText"
                        className="form-control form-control-sm"
                        rows="4"
                        placeholder="Paste or type extracted clinical plain text here..."
                        value={testFormattingSampleText}
                        onChange={e => setTestFormattingSampleText(e.target.value)}
                      />
                    </div>
                    <div className="text-right">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleTestRichTextFormattingPrompt}
                        disabled={isTestingFormattingPrompt}
                      >
                        {isTestingFormattingPrompt ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                            Formatting...
                          </>
                        ) : (
                          'Run Test'
                        )}
                      </button>
                    </div>

                    {testFormattingResults && (
                      <div className="mt-4">
                        <h6 className="font-weight-bold text-dark mb-2 small">Formatted Rich Text Preview:</h6>
                        <div
                          className="bg-white p-3 border rounded small mb-2"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(testFormattingResults.formattedHtml || ''),
                          }}
                        />
                        {testFormattingResults.formattedMarkdown && (
                          <details className="small text-muted">
                            <summary className="cursor-pointer font-weight-bold">View Raw Markdown Output</summary>
                            <pre
                              className="bg-white p-2 border rounded mt-1 mb-0 small text-dark"
                              style={{ whiteSpace: 'pre-wrap' }}
                            >
                              {testFormattingResults.formattedMarkdown}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Modal show={showSubmitModal} onHide={() => setShowSubmitModal(false)}>
        <Modal.Body>
          <h1 className="h6">File Initial Report</h1>
          <p>Ready to file and complete the initial report?</p>
          <div className="form-group">
            <label htmlFor="initSubmissionNote" className="form-label">
              Submission Note (Optional)
            </label>
            <textarea
              id="initSubmissionNote"
              className="form-control"
              rows={3}
              placeholder="Enter a note..."
              value={submissionNote}
              onChange={e => setSubmissionNote(e.target.value)}
            />
          </div>
          <div className="text-right">
            <button type="button" className="btn btn-secondary mr-1" onClick={() => setShowSubmitModal(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={onConfirmSubmit}>
              Confirm
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {confirmAction && (
        <ConfirmationModal
          title={confirmAction.title}
          body={confirmAction.body}
          onConfirm={confirmAction.onConfirm}
          onClose={() => setConfirmAction(null)}
        />
      )}

      {editingReport && (
        <Modal
          show={!!editingReport}
          onHide={() => setEditingReport(null)}
          centered
          onEntered={() => editTypeRef.current?.focus()}
        >
          <Modal.Header closeButton>
            <Modal.Title className="h6 font-weight-bold">
              Edit Report #{editingReport.reportNumber} Details (Admin)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group mb-3">
              <label htmlFor="editReportType" className="form-label small font-weight-bold text-muted">
                REPORT TYPE
              </label>
              <select
                ref={editTypeRef}
                id="editReportType"
                className="form-control"
                value={editFormData.reportType}
                onChange={e => setEditFormData({ ...editFormData, reportType: e.target.value })}
              >
                <option value="Initial">Initial Case Report</option>
                <option value="Progress">Progress Report</option>
                <option value="Final">Final Report</option>
                <option value="CaseManagement">Case Management Report</option>
              </select>
            </div>

            <div className="form-group mb-3">
              <label htmlFor="editSequenceNumber" className="form-label small font-weight-bold text-muted">
                SEQUENCE NUMBER
              </label>
              <input
                type="number"
                id="editSequenceNumber"
                className="form-control"
                min="1"
                value={editFormData.sequenceNumber}
                onChange={e => setEditFormData({ ...editFormData, sequenceNumber: e.target.value })}
              />
            </div>

            <div className="form-row mb-3">
              <div className="col-md-6">
                <label htmlFor="editStartDate" className="form-label small font-weight-bold text-muted">
                  ACTIVITY START DATE
                </label>
                <input
                  type="date"
                  id="editStartDate"
                  className="form-control"
                  value={editFormData.startUtc}
                  onChange={e => setEditFormData({ ...editFormData, startUtc: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="editEndDate" className="form-label small font-weight-bold text-muted">
                  ACTIVITY END DATE
                </label>
                <input
                  type="date"
                  id="editEndDate"
                  className="form-control"
                  value={editFormData.endUtc}
                  onChange={e => setEditFormData({ ...editFormData, endUtc: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group mb-3">
              <label htmlFor="editStatus" className="form-label small font-weight-bold text-muted">
                REPORT STATUS
              </label>
              <select
                id="editStatus"
                className="form-control"
                value={editFormData.status}
                onChange={e => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <option value="in-progress">{getStatusDisplay('in-progress', settings) || 'In Progress'}</option>
                <option value="in_smart_review">
                  {getStatusDisplay('in_smart_review', settings) || 'In Smart Review'}
                </option>
                <option value="submitted">{getStatusDisplay('submitted', settings) || 'Submitted'}</option>
                <option value="approved">{getStatusDisplay('approved', settings) || 'Approved'}</option>
                <option value="invoiced">{getStatusDisplay('invoiced', settings) || 'Invoiced'}</option>
                <option value="filed">{getStatusDisplay('filed', settings) || 'Filed'}</option>
                <option value="final">{getStatusDisplay('final', settings) || 'Final'}</option>
                <option value="rejected">{getStatusDisplay('rejected', settings) || 'Rejected'}</option>
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditingReport(null)}
              disabled={isSavingReport}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveReportDetails}
              disabled={isSavingReport}
            >
              {isSavingReport ? 'Saving...' : 'Save Changes'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

export default Component
